import { Position } from "@xyflow/react";
import { BaseNode, BaseNodeContent } from "../../components/base-node";
import { BaseHandle } from "../../components/base-handle";
import { TriggerNodeProps } from "./types";
import { isReturnTriggerType } from "./constants";
import { useTrackingStatusMap } from "./use-tracking-status-list";
import { useTranslation } from "react-i18next";
import { ContextualTipsTrigger } from "@babackapp/ui/components/contextual-tips";
import { Lock, Play } from "lucide-react";
import { useEditMode } from "../../context/edit-mode-context";
import {
  ClickAndCollectStatusChangeTriggerConfigDto,
  OrderNotFulfilledTriggerConfigDto,
  TrackingStatusChangeTriggerConfigDto,
} from "@babackapp/private-api-client/types";
import { ClaimStatusChangeTriggerConfigDto } from "../node-types";
import { TRIGGER_NODE_WIDTH, TRIGGER_NODE_HEIGHT_ESTIMATION } from "./constants";
import { isValidTrackingStatus } from "./utils";

export { TRIGGER_NODE_WIDTH, TRIGGER_NODE_HEIGHT_ESTIMATION };

function TrackingStatusContent({
  config,
}: {
  config: TrackingStatusChangeTriggerConfigDto;
}) {
  const trackingStatusMap = useTrackingStatusMap();

  const trackingStatus = config?.trackingStatus && isValidTrackingStatus(config.trackingStatus)
    ? trackingStatusMap[config.trackingStatus]
    : undefined;

  return (
    <div className="flex flex-col w-full gap-1">
      <span className="text-sm font-semibold flex-1">
        {trackingStatus?.label ?? config?.trackingStatus}
      </span>
      {trackingStatus?.customerDescription && (
        <p className="text-xs text-muted-foreground">
          {trackingStatus.customerDescription}
        </p>
      )}
    </div>
  );
}

function OrderNotFulfilledContent({
  config,
}: {
  config: OrderNotFulfilledTriggerConfigDto;
}) {
  const { t } = useTranslation("trackoffice");

  return (
    <div className="flex flex-col w-full gap-1">
      <span className="text-sm font-semibold flex-1">
        {t("workflowsEditor.panels.minDaysSinceCreation.title")}:{" "}
        {config?.minDaysSinceCreation || 3}
      </span>
      <p className="text-xs text-muted-foreground">
        {t("workflowsEditor.triggerTypes.order_not_fulfilled.description")}
      </p>
    </div>
  );
}

function ClickAndCollectStatusContent({
  config,
}: {
  config: ClickAndCollectStatusChangeTriggerConfigDto;
}) {
  const { t } = useTranslation("trackoffice");
  const status = config?.status;
  const statusKey = status
    ? `click-and-collect.status.${status.toLowerCase().replace(/_/g, "-")}`
    : undefined;

  return (
    <div className="flex flex-col w-full gap-1">
      <span className="text-sm font-semibold flex-1">
        {statusKey ? t(statusKey) : status}
      </span>
      <p className="text-xs text-muted-foreground">
        {t(
          "workflowsEditor.triggerTypes.click_and_collect_status_change.description",
        )}
      </p>
    </div>
  );
}

function ClaimStatusContent({
  config,
}: {
  config: ClaimStatusChangeTriggerConfigDto;
}) {
  const { t } = useTranslation("trackoffice");
  const statuses = config?.claimStatuses || [];

  return (
    <div className="flex flex-col w-full gap-1">
      <span className="text-sm font-semibold flex-1">
        {statuses
          .map((status: string) => t(`workflowsEditor.claimStatus.${status.toLowerCase()}`))
          .join(", ")}
      </span>
      <p className="text-xs text-muted-foreground">
        {t("workflowsEditor.triggerTypes.claim_status_change.description")}
      </p>
    </div>
  );
}

export function TriggerNodeEditor({ id, data }: TriggerNodeProps) {
  const { t } = useTranslation("trackoffice");
  const editMode = useEditMode();

  const renderContent = () => {
    // Widened copy: return triggers are extended types outside the generated
    // DTO union, so the exhaustive switch narrows `data` to never in default.
    const triggerType = data.triggerType as string;

    switch (data.triggerType) {
      case "tracking_status_change":
        return (
          <TrackingStatusContent
            config={data.config as TrackingStatusChangeTriggerConfigDto}
          />
        );
      case "order_not_fulfilled":
        return (
          <OrderNotFulfilledContent
            config={data.config as OrderNotFulfilledTriggerConfigDto}
          />
        );
      case "order_created":
        return (
          <p className="text-xs text-muted-foreground">
            {t("workflowsEditor.triggerTypes.order_created.description")}
          </p>
        );
      case "click_and_collect_status_change":
        return (
          <ClickAndCollectStatusContent
            config={data.config as ClickAndCollectStatusChangeTriggerConfigDto}
          />
        );
      case "claim_status_change":
        return (
          <ClaimStatusContent
            config={data.config as ClaimStatusChangeTriggerConfigDto}
          />
        );
      default:
        // Return triggers: the type is the event, nothing to configure —
        // render the descriptive text only.
        if (isReturnTriggerType(triggerType)) {
          return (
            <p className="text-xs text-muted-foreground">
              {t(`workflowsEditor.triggerTypes.${triggerType}.description`)}
            </p>
          );
        }
        return null;
    }
  };

  return (
    <ContextualTipsTrigger
      tips={[
        {
          id: "workflow-node-trigger",
          Icon: Play,
          title: t(
            "workflowsEditor.tips.workflowNodeTrigger.title",
            "This is a trigger node"
          ),
          description: t(
            "workflowsEditor.tips.workflowNodeTrigger.desc",
            "This node defines what should trigger the workflow. For example, you can trigger a workflow each time a specific tracking status is updated for a given parcel."
          ),
        },
      ]}
    >
      <BaseNode
        nodeId={id}
        title={t(`workflowsEditor.triggerTypes.${data.triggerType}.title`)}
        style={{ position: "relative" }}
      >
        <BaseNodeContent>
          {renderContent()}
          {editMode && (
            <div className="mt-2 flex items-center gap-1 border-t pt-2 text-[11px] text-muted-foreground">
              <Lock className="size-3 shrink-0" />
              <span>
                {t(
                  "workflowsEditor.triggerNode.lockedHint",
                  "Trigger type can't be changed.",
                )}
              </span>
            </div>
          )}
        </BaseNodeContent>
        <BaseHandle
          type="source"
          position={Position.Right}
          style={{ top: "50%", transform: "translateY(-50%)" }}
        />
      </BaseNode>
    </ContextualTipsTrigger>
  );
}
